// src/redux/slices/caseStudiesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { caseStudiesService } from '../../services/caseStudiesService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchCaseStudies = createAsyncThunk(
    'caseStudies/fetchCaseStudies',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudies(params)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchCaseStudyBySlug = createAsyncThunk(
    'caseStudies/fetchCaseStudyBySlug',
    async (slug, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudyBySlug(slug)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchFeaturedCaseStudies = createAsyncThunk(
    'caseStudies/fetchFeaturedCaseStudies',
    async (limit = 3, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudies({
                featured: true,
                limit
            })
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchCaseStudyCategories = createAsyncThunk(
    'caseStudies/fetchCaseStudyCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudyCategories()
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchCaseStudyIndustries = createAsyncThunk(
    'caseStudies/fetchCaseStudyIndustries',
    async (_, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.getCaseStudyIndustries()
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createCaseStudy = createAsyncThunk(
    'caseStudies/createCaseStudy',
    async (caseStudyData, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.createCaseStudy(caseStudyData)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateCaseStudy = createAsyncThunk(
    'caseStudies/updateCaseStudy',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await caseStudiesService.updateCaseStudy(id, data)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteCaseStudy = createAsyncThunk(
    'caseStudies/deleteCaseStudy',
    async (id, { rejectWithValue }) => {
        try {
            await caseStudiesService.deleteCaseStudy(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const caseStudiesSlice = createSlice({
    name: 'caseStudies',
    initialState: {
        caseStudies: [],
        featuredCaseStudies: [],
        currentCaseStudy: null,
        categories: [],
        industries: [],
        loading: false,
        creating: false,
        updating: false,
        deleting: false,
        error: null,
        hasMore: true
    },
    reducers: {
        clearCurrentCaseStudy: (state) => {
            state.currentCaseStudy = null
        },
        clearError: (state) => {
            state.error = null
        },
        clearCaseStudies: (state) => {
            state.caseStudies = []
            state.featuredCaseStudies = []
            state.hasMore = true
        },
        updateCaseStudyLocally: (state, action) => {
            const updatedCaseStudy = action.payload
            const index = state.caseStudies.findIndex(cs => cs._id === updatedCaseStudy._id)
            if (index !== -1) {
                state.caseStudies[index] = updatedCaseStudy
            }

            const featuredIndex = state.featuredCaseStudies.findIndex(cs => cs._id === updatedCaseStudy._id)
            if (featuredIndex !== -1) {
                state.featuredCaseStudies[featuredIndex] = updatedCaseStudy
            }

            if (state.currentCaseStudy && state.currentCaseStudy._id === updatedCaseStudy._id) {
                state.currentCaseStudy = updatedCaseStudy
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Case Studies
            .addCase(fetchCaseStudies.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchCaseStudies.fulfilled, (state, action) => {
                state.loading = false
                const newCaseStudies = action.payload.data.caseStudies || action.payload

                if (action.payload.page > 1) {
                    state.caseStudies = [...state.caseStudies, ...newCaseStudies]
                } else {
                    state.caseStudies = newCaseStudies
                }

                state.hasMore = newCaseStudies.length === (action.payload.pagination?.limit || 10)
                state.categories = action.payload.categories || state.categories
                state.industries = action.payload.industries || state.industries
                state.error = null
            })
            .addCase(fetchCaseStudies.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Fetch Case Study by Slug
            .addCase(fetchCaseStudyBySlug.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchCaseStudyBySlug.fulfilled, (state, action) => {
                state.loading = false
                state.currentCaseStudy = action.payload?.data?.caseStudy
                state.error = null
            })
            .addCase(fetchCaseStudyBySlug.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Fetch Featured Case Studies
            .addCase(fetchFeaturedCaseStudies.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchFeaturedCaseStudies.fulfilled, (state, action) => {
                state.loading = false
                state.featuredCaseStudies = action.payload.data || action.payload
                state.error = null
            })
            .addCase(fetchFeaturedCaseStudies.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Fetch Categories
            .addCase(fetchCaseStudyCategories.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchCaseStudyCategories.fulfilled, (state, action) => {
                state.loading = false
                state.categories = action.payload
                state.error = null
            })
            .addCase(fetchCaseStudyCategories.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Fetch Industries
            .addCase(fetchCaseStudyIndustries.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchCaseStudyIndustries.fulfilled, (state, action) => {
                state.loading = false
                state.industries = action.payload
                state.error = null
            })
            .addCase(fetchCaseStudyIndustries.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Create Case Study
            .addCase(createCaseStudy.pending, (state) => {
                state.creating = true
                state.error = null
            })
            .addCase(createCaseStudy.fulfilled, (state, action) => {
                state.creating = false
                state.caseStudies.unshift(action.payload)
                if (action.payload.isFeatured) {
                    state.featuredCaseStudies.unshift(action.payload)
                }
                state.error = null
            })
            .addCase(createCaseStudy.rejected, (state, action) => {
                state.creating = false
                state.error = action.payload
            })
            // Update Case Study
            .addCase(updateCaseStudy.pending, (state) => {
                state.updating = true
                state.error = null
            })
            .addCase(updateCaseStudy.fulfilled, (state, action) => {
                state.updating = false
                const updatedCaseStudy = action.payload
                const index = state.caseStudies.findIndex(cs => cs._id === updatedCaseStudy._id)
                if (index !== -1) {
                    state.caseStudies[index] = updatedCaseStudy
                }

                // Update featured case studies
                const featuredIndex = state.featuredCaseStudies.findIndex(cs => cs._id === updatedCaseStudy._id)
                if (featuredIndex !== -1) {
                    if (updatedCaseStudy.isFeatured) {
                        state.featuredCaseStudies[featuredIndex] = updatedCaseStudy
                    } else {
                        state.featuredCaseStudies.splice(featuredIndex, 1)
                    }
                } else if (updatedCaseStudy.isFeatured) {
                    state.featuredCaseStudies.unshift(updatedCaseStudy)
                }

                // Update current case study if it's the one being edited
                if (state.currentCaseStudy && state.currentCaseStudy._id === updatedCaseStudy._id) {
                    state.currentCaseStudy = updatedCaseStudy
                }

                state.error = null
            })
            .addCase(updateCaseStudy.rejected, (state, action) => {
                state.updating = false
                state.error = action.payload
            })
            // Delete Case Study
            .addCase(deleteCaseStudy.pending, (state) => {
                state.deleting = true
                state.error = null
            })
            .addCase(deleteCaseStudy.fulfilled, (state, action) => {
                state.deleting = false
                const deletedId = action.payload
                state.caseStudies = state.caseStudies.filter(cs => cs._id !== deletedId)
                state.featuredCaseStudies = state.featuredCaseStudies.filter(cs => cs._id !== deletedId)

                if (state.currentCaseStudy && state.currentCaseStudy._id === deletedId) {
                    state.currentCaseStudy = null
                }

                state.error = null
            })
            .addCase(deleteCaseStudy.rejected, (state, action) => {
                state.deleting = false
                state.error = action.payload
            })
    }
})

export const {
    clearCurrentCaseStudy,
    clearError,
    clearCaseStudies,
    updateCaseStudyLocally
} = caseStudiesSlice.actions

export default caseStudiesSlice.reducer